import CreateIcon from "@mui/icons-material/AddCircle";
import {
  Box,
  Dialog,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Button, Spacer } from "common";
import I18n from "i18n-js";
import { createContext, useContext, useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { PERMISSIONS } from "../../app/constants";
import useAccess from "../profile/useAccess";
import { SharingListItem } from "../sharings/sharingApi";
import SharingForm from "../sharings/SharingForm";
import useSharingList from "../sharings/useSharingList";

// Context for sharing sharings data between parent and child components
interface SharingContextType {
  sharings: SharingListItem[];
  selectedSharing: SharingListItem | null;
  loading: boolean;
  editSharing: (sharing: SharingListItem) => void;
  deleteSharing: (sharing: SharingListItem) => void;
}

const SharingContext = createContext<SharingContextType | null>(null);

export const useSharingContext = () => {
  const context = useContext(SharingContext);
  if (!context) {
    throw new Error("useSharingContext must be used within SharingProvider");
  }
  return context;
};

// Main component that provides context and renders tabs
export default function WaiverSharings() {
  const { waiver_id, sharing_id } = useParams();
  const navigate = useNavigate();
  const access = useAccess();
  const grid = useSharingList({ waiver_id });
  const [selectedSharing, setSelectedSharing] =
    useState<SharingListItem | null>(null);

  // Update selected sharing when sharing_id or data changes
  useEffect(() => {
    if (grid.rows && grid.rows.length > 0) {
      if (sharing_id) {
        const sharing = grid.rows.find(s => s.id.toString() === sharing_id);
        if (sharing) {
          setSelectedSharing(sharing);
        } else {
          // If sharing not found, redirect to first sharing
          const firstSharing = grid.rows[0];
          setSelectedSharing(firstSharing);
          navigate(`/waivers/${waiver_id}/publish/${firstSharing.id}`, {
            replace: true,
          });
        }
      } else {
        // If no sharing_id, redirect to first sharing
        const firstSharing = grid.rows[0];
        setSelectedSharing(firstSharing);
        navigate(`/waivers/${waiver_id}/publish/${firstSharing.id}`, {
          replace: true,
        });
      }
    }
  }, [grid.rows, sharing_id, waiver_id, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const sharing = grid.rows[newValue];
    if (sharing) {
      setSelectedSharing(sharing);
      navigate(`/waivers/${waiver_id}/publish/${sharing.id}`);
    }
  };

  const handleEdit = (sharing: SharingListItem) => {
    console.log({ sharing });
    grid.setEditItem(sharing);
    //grid.editItemDialog.onOpen();
  };

  const handleCreate = () => {
    grid.setEditItem({ waiver_ids: [waiver_id], id: "create" } as any);
    grid.editItemDialog.onOpen();
  };

  const handleDelete = (sharing: SharingListItem) => {
    grid.deleteItem(sharing);
  };

  const getTabValue = () => {
    if (!selectedSharing || !grid.rows) return 0;
    return grid.rows.findIndex(s => s.id === selectedSharing.id);
  };

  if (grid.loading) {
    return <LinearProgress />;
  }

  const contextValue: SharingContextType = {
    sharings: grid.rows,
    selectedSharing,
    loading: grid.loading,
    editSharing: handleEdit,
    deleteSharing: handleDelete,
  };

  return (
    <SharingContext.Provider value={contextValue}>
      <Box>
        {/* Sharing Tabs */}
        {!grid.rows || grid.rows.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {I18n.t("Sharing.noSharings")}
            </Typography>
            <Spacer h={2} />
            <Button
              title={I18n.t("Sharing.addTitle")}
              startIcon={<CreateIcon />}
              onClick={() => handleCreate()}
            />
          </Box>
        ) : (
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Tabs
              value={getTabValue()}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {grid.rows.map(sharing => {
                let name = sharing.name;
                if (name.length > 20) {
                  name = name.substring(0, 17) + "...";
                }
                return <Tab key={sharing.id} label={name} />;
              })}
            </Tabs>
            <IconButton onClick={() => handleCreate()}>
              <CreateIcon color="primary" />
            </IconButton>
          </Box>
        )}

        {/* Outlet for nested routes or default content */}
        <Outlet />
      </Box>
      {/* Edit Dialog */}
      <Dialog
        open={grid.editItemDialog.open}
        onClose={grid.editItemDialog.onClose}
        maxWidth="sm"
        fullWidth
      >
        <SharingForm
          id={grid.editItem?.id?.toString()}
          afterSave={() => {
            grid.editItemDialog.onClose();
            grid.load();
          }}
          onClose={grid.editItemDialog.onClose}
          disabled={!access.hasPermissions(PERMISSIONS.MANAGE_SHARINGS)}
          defaultValues={
            waiver_id ? { waiver_ids: [parseInt(waiver_id)] } : undefined
          }
        />
      </Dialog>
    </SharingContext.Provider>
  );
}
